import { useCallback, useEffect, useRef, useState } from 'react'
import { EditionsView } from './tournament/editions'
import { ParticipantView } from './tournament/participant'
import { ParticipantsView } from './tournament/participants'
import { navigationUrl, phases, statuses } from './tournament/shared'
import type { Navigate, Navigation, PhaseFilter, StatusFilter } from './tournament/shared'

function readPage(value: string | null) {
  const number = Number(value)
  return Number.isSafeInteger(number) && number > 0 ? number : 1
}

function readNavigation(): Navigation {
  const params = new URLSearchParams(window.location.search)
  const phase = params.get('tournamentPhase') as PhaseFilter | null
  const status = params.get('participantStatus') as StatusFilter | null
  const editionId = params.get('tournament')
  return {
    editionId,
    participantId: editionId ? params.get('participant') : null,
    editionPage: readPage(params.get('tournamentPage')),
    phase: phase && phases.includes(phase) ? phase : 'ALL',
    participantPage: readPage(params.get('participantPage')),
    search: (params.get('tournamentSearch') ?? '').slice(0, 200),
    status: status && statuses.includes(status) ? status : 'ALL',
  }
}

function scrollToTournament() {
  window.requestAnimationFrame(() => {
    document.getElementById('tournaments')?.scrollIntoView({ block: 'start', behavior: 'instant' })
  })
}

export function TournamentSection() {
  const [navigation, setNavigation] = useState(readNavigation)
  const navigationRef = useRef(navigation)
  const navigate = useCallback<Navigate>((patch, replace = false, scroll = true) => {
    const next = { ...navigationRef.current, ...patch }
    const url = navigationUrl(navigationRef.current, patch)
    if (replace)
      window.history.replaceState(null, '', url)
    else
      window.history.pushState(null, '', url)
    navigationRef.current = next
    setNavigation(next)
    if (scroll)
      scrollToTournament()
  }, [])

  useEffect(() => {
    const update = () => {
      const next = readNavigation()
      navigationRef.current = next
      setNavigation(next)
      if (window.location.hash === '#tournaments' || next.editionId)
        scrollToTournament()
    }
    window.addEventListener('popstate', update)
    window.addEventListener('hashchange', update)
    if (window.location.hash === '#tournaments' || navigationRef.current.editionId)
      scrollToTournament()
    return () => {
      window.removeEventListener('popstate', update)
      window.removeEventListener('hashchange', update)
    }
  }, [])

  return (
    <div className="mx-auto min-w-0 max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {navigation.editionId
        ? navigation.participantId
          ? <ParticipantView key={`${navigation.editionId}:${navigation.participantId}`} navigation={navigation} navigate={navigate} />
          : <ParticipantsView key={navigation.editionId} navigation={navigation} navigate={navigate} />
        : <EditionsView navigation={navigation} navigate={navigate} />}
    </div>
  )
}
